<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="3"
        :min-h="minHeight"
        :resized="handleResized"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title>
            {{ title }}
        </template>
        <template v-slot:card-body>
            <card-grid
                ref="grid"
                :key="cardGridKey"
                :is-search="false"
                :columnDefs="columnDefs"
                :fetch-callback="fetchCallback"
                :pagination="true"
                :pagination-page-size="limit"
                :cache-block-size="limit"
                :is-auto-width="isAutoWidth"
                :font-size="tabWidget.settings.table_font_size"
            ></card-grid>
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
                :withTableFontSize="true"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
    import API from '../../../../api';
    import CardGrid from '../../../generic/card-grid/card-grid.vue';
    import CardGridItem from '../common/card-grid-item.vue';
    import {
        DASHBOARD_INQUIRY_GRID_ENDPOINT,
    } from '../../../../endpoints';
    import { escapeHTML } from '../../../../utils/strings';
    import { getWidgetGridRowsNumber } from '../helpers';
    import GuestCellRenderer from './guest-cell-renderer.vue';
    import isNil from 'lodash/isNil';
    import NoteCellRenderer from './note-cell-renderer.vue';
    import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
    import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

    export default {
        name: 'InquiriesTableCardGridItem',
        mixins: [cardGridItemBaseMixin],
        components: {
            CardGrid,
            CardGridItem,
            TabWidgetSettingsModalForm,
            noteCellRenderer: NoteCellRenderer,
            guestCellRenderer: GuestCellRenderer,
        },
        computed: {
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    return API.get(DASHBOARD_INQUIRY_GRID_ENDPOINT(), {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                        },
                    });
                };
            },
            isAutoWidth() {
                return this.tabWidget.w < 10;
            },
            tabWidgetSettingsInitialValues() {
                return {
                    settings: {
                        name: this.tabWidget.settings.name || this.defaultName,
                        table_font_size: this.tabWidget.settings.table_font_size || this.defaultWidgetFontSize,
                    },
                };
            },
            columnDefs() {
                return [
                    {
                        field: 'created_user',
                        headerName: 'Received',
                        type: 'dateColumn',
                        sort: 'desc',
                        minWidth: 140,
                    },
                    {
                        field: 'source_name',
                        headerName: 'Source',
                        cellRenderer: ({ data }) => {
                            const escapedSourceName = escapeHTML(data.source_name);
                            if (isNil(data.remote_id_link)) {
                                return 'N/A';
                            } else if (data.remote_id_link === '') {
                                return escapedSourceName;
                            } else {
                                return data.remote_id_link
                                    ? `<a target="_blank" href="${data.remote_id_link}">${escapedSourceName}</a>`
                                    : escapedSourceName;
                            }
                        },
                        minWidth: 130,
                    },
                    {
                        field: 'property',
                        headerName: 'Property',
                        cellRenderer: ({ data }) => {
                            return data.rental_property_name
                                ? escapeHTML(data.rental_property_name)
                                : 'Not Available';
                        },
                        minWidth: 280,
                    },
                    {
                        field: 'from_date',
                        headerName: 'Arrival',
                        type: 'dateColumn',
                        // minWidth: 140,
                    },
                    {
                        field: 'nts',
                        headerName: 'Nts',
                        minWidth: 90,
                        maxWidth: 90,
                        sortable: false,
                    },
                    {
                        field: 'guest',
                        headerName: 'Guest',
                        headerClass: 'guest-name-status-column',
                        cellRenderer: 'guestCellRenderer',
                        cellRendererParams: {
                            getGuest({ data }) {
                                return {
                                    id: data.guest.id,
                                    status: data.guest.status,
                                    displayName: data.guest.short_name,
                                    inquiryId: data.id,
                                    conversationLink: data.conversation_link,
                                };
                            },
                        },
                        minWidth: 190,
                    },
                    {
                        field: 'last_message_object.event',
                        headerName: 'Event',
                        // minWidth: 170,
                        sortable: false,
                    },
                    {
                        field: 'last_message_object.created_user',
                        headerName: 'Date',
                        type: 'dateTimeColumn',
                        minWidth: 190,
                        sortable: false,
                    },
                ];
            },

        },
        data() {
            return {
                autoWidthExclude: ['guest'],
                offset: 0,
                limit: getWidgetGridRowsNumber({ tabWidget: this.tabWidget }),
                sort: 'created',
                desc: false,
                search: '',
            };
        },
        methods: {
            refresh() {
                this.$refs.grid.refreshServerSide();
            },
            handleResized() {
                this.limit = getWidgetGridRowsNumber({ tabWidget: this.tabWidget });
                this.cardGridKey += 1;
            },
        },
    };
</script>
